// (Note: TaskStatus type preserved as requested)
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'pantry' | 'finalized' | 'history' | 'done';

import React, { useMemo, useState } from 'react';
import { addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, format } from 'date-fns';
import { writeBatch, doc, collection } from 'firebase/firestore';
import { db } from '@/firebase';

// --- Types & Bricks ---
import { 
  AppState, 
  AppAction, 
  HouseholdMember, // 🎯 Ensure HouseholdMember is imported from '@/types'
  DataScope,
  Role,            // 🎯 Role is needed for default
  UserProfile      // 🎯 UserProfile is needed for lookup
} from '@/types';
import { 
  CalendarEvent as EngineEvent, 
  CustomCalendar,
} from '@/types';
import { CalendarToolbar } from '../features/calendar-engine/components/CalendarToolbar';
import { CalendarGrid } from '../features/calendar-engine/components/CalendarGrid';
import { EventModal } from '../features/calendar-engine/components/EventModal';
import { RecurrenceActionDialog, RecurrenceChoice } from '../features/calendar-engine/components/RecurrenceActionDialog';
import { CustomCalendarCreator } from '../features/calendar-engine/components/CustomCalendarCreator';

// --- Logic Engines ---
import { CalendarEngine } from '../features/calendar-engine/utils/calendarEngine';
import { useCalendarState } from '../features/calendar-engine/hooks/useCalendarState';

interface CalendarProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

function isEngineEvent(event: EngineEvent | CustomCalendar): event is EngineEvent {
  return (event as EngineEvent).start !== undefined; // Check for a property unique to CalendarEvent
}

export const Calendar: React.FC<CalendarProps> = ({ state, dispatch }) => {
  const userTz = state.currentUser?.timezone || 'UTC';
  const currentUserId = state.currentUser?.id || '';
  const hId = state.currentHouseholdId;
  const engine = useMemo(() => new CalendarEngine(15), []);

  // --- 🛰️ HYBRID SCOPE STATE ---
  // Default to BOTH if in a household, otherwise lock to PERSONAL
  const [activeScope, setActiveScope] = useState<DataScope>(hId ? 'BOTH' : 'PERSONAL');
  const [view, setView] = useState<'day' | 'week' | 'month'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeCalendarIds, setActiveCalendarIds] = useState<string[]>(['personal', 'household']);
  const [activeMembers, setActiveMembers] = useState<string[]>([currentUserId]);
  
  // Intelligence Layer Toggles
  const [isTravelEnabled, setTravelEnabled] = useState(false);
  const [isGhostLayerEnabled, setGhostLayerEnabled] = useState(false);

  // Modal/Dialog Control
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isCustomCreatorOpen, setIsCustomCreatorOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Partial<EngineEvent> | null>(null);
  
  const [recurrenceDialog, setRecurrenceDialog] = useState<{ 
    isOpen: boolean; 
    mode: 'edit' | 'delete'; 
    event: EngineEvent | null 
  }>({
    isOpen: false,
    mode: 'edit',
    event: null
  });

  const currentHousehold = state.households?.find(h => h.id === hId);

  // 🎯 DERIVED MEMBERS FOR DISPLAY (Similar to HouseholdSettings.tsx)
  const householdMembersForDisplay = useMemo(() => {
    if (!currentHousehold || !state.userProfiles) return [];

    return currentHousehold.memberIds.map(memberId => {
      const userProfile = state.userProfiles.find(p => p.id === memberId);
      const memberRole = currentHousehold.permissions?.[memberId]?.role || Role.MEMBER;
      const memberJoinedAt = currentHousehold.permissions?.[memberId]?.joinedAt || new Date().toISOString();

      return {
        userId: memberId,
        name: userProfile?.name || 'Unknown User',
        email: userProfile?.email || 'N/A',
        role: memberRole,
        joinedAt: memberJoinedAt,
      } as HouseholdMember;
    });
  }, [currentHousehold, state.userProfiles]);

  // --- 🧠 CONTEXTUAL DATA MERGE ---
  // useCalendarState updated to filter events based on activeScope
  const { events, hasConflicts } = useCalendarState(
    (state as any).personalEvents || [], 
    (state as any).householdEvents || [],
    (state as any).customEvents || [],
    activeCalendarIds,
    currentUserId,
    hId, // 🎯 Pass currentHouseholdId (hId) here
    activeMembers,
    activeScope // 🛡️ Scope aware filtering
  );

  const processedEvents = useMemo(() => {
    const householdAddress = currentHousehold?.address;
    const formattedAddress = householdAddress && householdAddress.street
      ? `${householdAddress.street}, ${householdAddress.city}, ${householdAddress.state} ${householdAddress.zip}`.trim()
      : undefined;

    const filteredEvents: EngineEvent[] = events.filter(isEngineEvent);

    return engine.processInstances(
      filteredEvents,
      userTz,
      currentUserId,
      formattedAddress ?? null,
      isTravelEnabled
    );
  }, [events, userTz, isTravelEnabled, currentUserId, currentHousehold, engine]);
  
  // --- 🛠️ PERSISTENCE HANDLERS ---
  const handleSaveEvent = async (eventData: any) => {
    const batch = writeBatch(db);
    
    // 🛡️ DATA PATH RESOLUTION
    // If no hId exists, force 'personal'. Otherwise, check eventData.scope
    const isPersonalScope = !hId || eventData.scope === 'personal';
    
    // 🔒 PERMISSION CHECK for Household writes
    if (!isPersonalScope) {
      const permissions = currentHousehold?.permissions?.[currentUserId];
      if (!permissions?.canManageEvents) {
        alert("Permission Denied: You cannot write to the Household agenda.");
        return;
      }
    }

    const collectionPath = isPersonalScope 
      ? `users/${currentUserId}/events`
      : `households/${hId}/events`;

    const eventRef = eventData.id 
      ? doc(db, collectionPath, eventData.id)
      : doc(collection(db, collectionPath));

    batch.set(eventRef, { 
      ...eventData, 
      id: eventRef.id,
      scope: isPersonalScope ? 'personal' : 'household',
      ownerId: isPersonalScope ? currentUserId : hId
    }, { merge: true });

    await batch.commit();
    setIsEventModalOpen(false);
  };

  // --- 🕹️ NAVIGATION ---
  const handleNavigate = (dir: 'back' | 'forward' | 'today') => {
    if (dir === 'today') return setCurrentDate(new Date());
    const step = dir === 'forward' ? 1 : -1;
    if (view === 'day') setCurrentDate(prev => step > 0 ? addDays(prev, 1) : subDays(prev, 1));
    if (view === 'week') setCurrentDate(prev => step > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    if (view === 'month') setCurrentDate(prev => step > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

    const handleEventClick = (event: EngineEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  return (
    <div className="flex flex-col h-screen w-full bg-white dark:bg-slate-950 overflow-hidden calendar-view-lock">
      
      <CalendarToolbar 
        view={view}
        onViewChange={setView}
        onToday={() => handleNavigate('today')}
        onPrev={() => handleNavigate('back')}
        onNext={() => handleNavigate('forward')}
        currentDateLabel={format(currentDate, 'MMMM yyyy')}
        isTravelEnabled={isTravelEnabled}
        setTravelEnabled={setTravelEnabled}
        isGhostLayerEnabled={isGhostLayerEnabled}
        setGhostLayerEnabled={setGhostLayerEnabled}
        activeMembers={activeMembers}
        onMemberToggle={(id) => setActiveMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id])}
        householdMembers={householdMembersForDisplay}
        customCalendars={(state as any).customCalendars || []}
        activeCalendarIds={activeCalendarIds}
        onCalendarToggle={(id) => setActiveCalendarIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
        onAddNewCalendar={() => setIsCustomCreatorOpen(true)}
        hasConflicts={hasConflicts}
        activeScope={activeScope}
        onScopeChange={setActiveScope}
        currentHouseholdId={hId}
      />

      <div className="flex-1 relative overflow-hidden">
        <CalendarGrid
          view={view}
          events={processedEvents}
          activeMembers={activeMembers}
          isTravelEnabled={isTravelEnabled}
          isGhostLayerEnabled={isGhostLayerEnabled}
          hourHeight={80}
          householdMembers={householdMembersForDisplay}
          currentUserId={currentUserId}
          currentHouseholdId={hId}
          onEventClick={handleEventClick}
          activeScope={activeScope}
          currentDate={currentDate}
          onViewChange={setView} 
          onDateChange={setCurrentDate}    
        />
      </div>

      <EventModal 
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        onSave={handleSaveEvent}
        initialData={selectedEvent || {}}
        userTimezone={userTz}
        state={state}
        contacts={currentHousehold?.contacts || []}
        defaultScope={activeScope === 'SHARED' ? 'household' : 'personal'}
      />

      {/* creator and dialog components remain as they do not impact path changes */}
      <CustomCalendarCreator 
        isOpen={isCustomCreatorOpen}
        onClose={() => setIsCustomCreatorOpen(false)}
        currentUserId={currentUserId}
        currentHouseholdId={hId} // 🎯 Pass currentHouseholdId
        onSave={(newCal: Partial<CustomCalendar>) => {
          console.log("Creating Custom Layer:", newCal);
        }}
      />

      <RecurrenceActionDialog 
        isOpen={recurrenceDialog.isOpen}
        mode={recurrenceDialog.mode}
        onClose={() => setRecurrenceDialog({ ...recurrenceDialog, isOpen: false })}
        onSelect={(choice: RecurrenceChoice) => {
          console.log("Recurrence Strategy Selected:", choice);
          setRecurrenceDialog({ ...recurrenceDialog, isOpen: false });
        }}
        eventScope={recurrenceDialog.event?.scope || 'personal'}
      />
    </div>
  );
};